import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FHTooltip } from '../../../components/base';
import StatusIconRenderer from '../../../components/layout/StatusIconRenderer';
import { ServiceCodes } from '../../../components/layout/billings/claims/constants';
import { TreatmentPlanStatus } from '../../../components/layout/treatment-plan/constants/general';
import {
  capitalizeFirstLetter,
  formatEntityId,
  formatSecondsToHHMM,
  history,
} from '../../../helpers';
import { FORTA_BLUE, FORTA_GRAY1, FORTA_WHITE } from '../../../utils/colors';
import {
  ActionRequiredItemEnum,
  BTSessionStatus,
  DATE_DISPLAY_FORMAT,
  SupervisionNoteStatus,
} from '../../../constants';
import { ActionReqTypeMap, cardChipStyles } from '../constants';
import {
  getActionRequiredItemTimeLabel,
  getClientLabelForActionRequiredItem,
  getPrefixForActionRequiredCardId,
} from '../utils';
import EditIcon from '../../../assets/images/edit-new.svg';
import ProgressIcon from '../../../assets/images/progress-new.svg';

import type {
  IBtSupervision,
  IClientAssessment,
  IClientSupervision,
  IGroupSupervision,
  IParentSupervision,
  ISessionMinified,
  ITreatmentPlan,
} from '../../../utils/types';
import type { Moment } from 'moment';

export type ActionRequiredItem =
  | IBtSupervision
  | IClientSupervision
  | IGroupSupervision
  | IParentSupervision
  | ITreatmentPlan
  | IClientAssessment
  | ISessionMinified;

interface ActionRequiredCardProps {
  item: ActionRequiredItem;
  dateStart: string | Date | Moment | null;
  type: ActionRequiredItemEnum;
  status: string;
  duration?: number;
  id: string | number;
}

function ActionRequiredCard({
  item,
  dateStart,
  type,
  id,
  status,
  duration,
}: ActionRequiredCardProps) {
  const clientName = getClientLabelForActionRequiredItem(item, type);
  const dateLabel = getActionRequiredItemTimeLabel(dateStart, type);
  const { name, color } = ActionReqTypeMap[type];
  const statusValue =
    Object.values(BTSessionStatus).find((value) => value === status) ??
    Object.values(SupervisionNoteStatus).find((value) => value === status);

  const handleClick = () => {
    switch (type) {
      case ActionRequiredItemEnum.Client:
        history.navigate(`/client-supervisions/${id}`);
        break;
      case ActionRequiredItemEnum.Bt:
        history.navigate(`/bt-supervisions/${id}`);
        break;
      case ActionRequiredItemEnum.Parent:
        history.navigate(`/parent-supervisions/${id}`);
        break;
      case ActionRequiredItemEnum.Assessment:
        history.navigate(`/assessment-note/${id}`);
        break;
      case ActionRequiredItemEnum.GroupParent:
        history.navigate(`/group-parent-training/${id}`);
        break;
      case ActionRequiredItemEnum.TreatmentPlan:
        history.navigate(`/treatment-plan/${id}/client-info`);
        break;
      case ActionRequiredItemEnum.Session:
        history.navigate(`/sessions/${id}`);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 2,
        backgroundColor: FORTA_WHITE,
        borderRadius: '8px',
        mb: 1,
        mr: 1,
        cursor: 'pointer',
        border: `1px solid ${FORTA_BLUE.light1}`,
        '&:hover': {
          boxShadow: `1px 2px 4px rgba(0, 0, 0, 0.2)`,
        },
      }}
      data-testid={
        type === ActionRequiredItemEnum.TreatmentPlan && 'is_active' in item
          ? `treatmentPlan-${item.client?.first_name}_${item.is_active}_${status}`
          : `supervision-${clientName.replace(/ /g, '_')}-${status}-${type}`
      }
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography textAlign="left" fontWeight={600} color={FORTA_BLUE.dark}>
          {clientName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FHTooltip
            sx={{ inset: 'auto auto 6px 0px' }}
            title={`${
              type === ActionRequiredItemEnum.TreatmentPlan
                ? dateStart
                  ? 'TP proposed start date: '
                  : 'TP proposed start date not available'
                : ''
            }${dateStart ? moment(dateStart).format(DATE_DISPLAY_FORMAT) : ''} ${
              duration ? ` - ${formatSecondsToHHMM(duration)}` : ''
            }`}
            arrow
            placement="top"
            maxwidth={360}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_GRAY1}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <Typography fontWeight={300} fontSize={14} color={FORTA_BLUE.dark} mr={1} mt={-0.5}>
              {dateLabel}
            </Typography>
          </FHTooltip>
          <FHTooltip
            sx={{ inset: 'auto auto 6px 0px' }}
            title={capitalizeFirstLetter(status.toLowerCase())}
            arrow
            placement="top"
            maxwidth={360}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_GRAY1}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <span>
              {type === ActionRequiredItemEnum.TreatmentPlan ? (
                status === TreatmentPlanStatus.Editing ? (
                  <img src={EditIcon} alt="Edit" width={24} height={24} />
                ) : (
                  <img src={ProgressIcon} alt="Progress" width={24} height={24} />
                )
              ) : (
                <StatusIconRenderer
                  iconHeight={24}
                  iconWidth={24}
                  procedureCode={ServiceCodes.SessionProcedureCode}
                  status={statusValue}
                />
              )}
            </span>
          </FHTooltip>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: color,
              mr: 0.5,
              mt: 1,
            }}
          />
          <Typography fontWeight={300} fontSize={14} mt={1}>
            {name}
          </Typography>
        </Box>
        <Typography sx={{ ...cardChipStyles, mr: 0 }} mt={1}>
          {formatEntityId(id, getPrefixForActionRequiredCardId(type))}
        </Typography>
      </Box>
    </Box>
  );
}

export default ActionRequiredCard;
